import React, { useEffect } from 'react';
import { getLocalUserData } from '../../utils/user';
import { useConfigurationSections } from '../../hooks/useConfiguration';

let messengerStateTracked = false;

/**
 * Subscribes to messenger open/close events so we can avoid calling
 * Messenger.close when the window is already closed (which throws in the SDK).
 */
function setupMessengerStateTracking() {
    if (messengerStateTracked || !window.Genesys) return;
    messengerStateTracked = true;
    window.Genesys('subscribe', 'Messenger.opened', () => {
        Genesys.messengerOpen = true;
    });
    window.Genesys('subscribe', 'Messenger.closed', () => {
        Genesys.messengerOpen = false;
    });
}

/**
 * Genesys Controller Object
 *
 * Provides methods and state management for the Genesys Web Messenger.
 * This object serves as the main interface for controlling chat functionality.
 */
export const Genesys = {
    /** Current language setting for the chat widget */
    currentLanguage: 'en' as 'en' | 'ar',

    /** Indicates whether Genesys has been fully initialized and is ready for commands */
    ready: false,

    /** Tracks whether the messenger window is currently open */
    messengerOpen: false,

    /**
     * Gets the Genesys messenger DOM element
     * @returns The messenger container element or null if not found
     */
    get element() {
        return document.getElementById('genesys-messenger');
    },

    /**
     * Opens the Genesys chat messenger window
     * Only works if Genesys is ready and initialized
     */
    openChat() {
        if (!Genesys.ready) return;
        window.Genesys('command', 'Messenger.open');
    },

    /**
     * Closes the Genesys chat messenger window
     * Only works if Genesys is ready and initialized
     */
    closeChat() {
        if (!Genesys.ready || !Genesys.messengerOpen) return;
        window.Genesys('command', 'Messenger.close');
    },

    /**
     * Completely hides the chat widget by closing the messenger and hiding the launcher
     * This removes the chat from view entirely
     */
    hide() {
        Genesys.closeChat();
        Genesys.setVisibility(false);
    },

    /**
     * Shows the chat widget by making the launcher visible
     * Users can then click the launcher to open the messenger
     */
    show() {
        Genesys.setVisibility(true);
    },

    /**
     * Controls the visibility of the chat launcher button
     * @param visible - Whether the launcher should be visible
     */
    setVisibility(visible: boolean) {
        if (Genesys.element) {
            Genesys.element.style.display = visible ? 'block' : 'none';
        }
    },

    setCustomAttributes(customAttributes: Partial<GenesysCustomAttributes>) {
        window.Genesys('command', 'Database.set', {
            messaging: {
                customAttributes,
            },
        });
    },

    /**
     * Sets the z-index CSS property of the chat widget to control stacking order
     * @param zIndex - The desired z-index value
     */
    setZIndex(zIndex: string) {
        if (Genesys.element) {
            Genesys.element.style.zIndex = zIndex;
            Genesys.element.style.position = 'relative'; // To ensure zIndex takes effect
        }
    },
};

interface GenesysConfigCMS {
    enabled: boolean;
    environment: string;
    scriptSrc: string;
    deploymentId: {
        ar?: string;
        en?: string;
        Ar?: string;
        En?: string;
    };
    extra?: {
        zIndex?: string;
        customAttributes?: {
            environment?: string;
        };
    };
}

interface GenesysSetupConfig {
    environment: string;
    deploymentId: string;
}

interface GenesysCustomAttributes {
    name: string; // Username
    username: string; // Username again
    email: string; // Email address
    mobile: string; // Mobile number
    userId: string; // UID or Blank (null until PDPL is implemented)
    subscriptionTier: string; // e.g., Free, Premium, etc.
    nextRenewalDate: string; // ISO date string
    language: 'Ar' | 'En'; // Language preference
    channel: 'Web' | 'Mobile'; // Channel of interaction
    environment: string; // e.g., Production, Staging
}

/**
 * Dynamically loads the Genesys Web Messenger script and initializes it
 *
 * This function follows the Genesys integration pattern:
 * 1. Sets up the global Genesys function as a command queue
 * 2. Loads the external Genesys script asynchronously
 * 3. Waits for the launcher to be ready before executing callbacks
 *
 * @param scriptSrc - URL to the Genesys script (from configuration)
 * @param config - Genesys configuration object containing environment and deploymentId
 * @param onGenesysReady - Callback fired when Genesys launcher is ready for commands
 * @param onError - Callback fired if script loading fails
 */
function loadGenesysScript(
    scriptSrc: string,
    config: GenesysSetupConfig,
    onGenesysReady: () => void,
    onError?: () => void
) {
    // Attach Genesys config to a global variable
    window['_genesysJs'] = 'Genesys';

    // If the Genesys function doesn't exist, create it as a queueing function
    if (!window.Genesys) {
        window.Genesys = function () {
            // eslint-disable-next-line prefer-rest-params
            (window.Genesys.q = window.Genesys.q || []).push(arguments);
        } as GenesysFunction;
    }
    setupMessengerStateTracking();

    // Record the initialization timestamp
    window.Genesys.t = Date.now();

    // Attach the Genesys config object
    window.Genesys.c = config;

    // Create and append the Genesys script to the document head
    const script = document.createElement('script');
    script.async = true;
    script.src = scriptSrc;
    script.charset = 'utf-8';

    script.onload = () => {
        // Wait for the launcher plugin to be fully ready before proceeding
        window.Genesys('subscribe', 'Launcher.ready', function () {
            Genesys.ready = true;
            onGenesysReady();
        });
    };
    script.onerror = () => {
        onError?.();
    };

    document.head.appendChild(script);
}

/**
 * Initializes Genesys Web Messenger with configuration from app settings
 *
 * This function:
 * 1. Retrieves Genesys configuration from the app config
 * 2. Determines the appropriate language/deployment ID
 * 3. Loads the Genesys script with proper configuration
 * 4. Sets custom attributes when the Genesys launcher is ready
 */
function initializeGenesys(config: { genesys?: GenesysConfigCMS }) {
    const genesysConfigCMS = config?.genesys ?? null;
    if (!genesysConfigCMS?.enabled || !genesysConfigCMS?.scriptSrc) return;

    const language = window.location.pathname.startsWith('/en') ? 'en' : 'ar';
    Genesys.currentLanguage = language;

    const userData = getLocalUserData();
    const tierName = userData?.tierConfig?.tierName;
    const plan = { Free: 'Basic', Plus: 'Premium', Paid: 'Premium' }[tierName] || tierName;

    const nextRenewalTimestamp = userData?.tierStatus?.nextRenewalDate;
    const nextRenewalDate = nextRenewalTimestamp ? new Date(nextRenewalTimestamp).toUTCString() : 'Unknown';

    const deploymentId =
        genesysConfigCMS.deploymentId?.[language] ?? genesysConfigCMS.deploymentId?.[language === 'en' ? 'En' : 'Ar'];

    if (!deploymentId) {
        return;
    }

    loadGenesysScript(
        genesysConfigCMS.scriptSrc,
        {
            environment: genesysConfigCMS.environment,
            deploymentId,
        },
        () => {
            const zIndex = genesysConfigCMS?.extra?.zIndex || 'default';
            if (zIndex !== 'default') Genesys.setZIndex(zIndex);
            Genesys.setCustomAttributes({
                ...(genesysConfigCMS?.extra?.customAttributes || {}),
                name: userData?.username || '', // Username
                username: userData?.username || '', // Username again
                email: userData?.email || '', // Email address
                mobile: userData?.msisdn || '', // Mobile number
                userId: '', // UID or Blank (null until PDPL is implemented)
                subscriptionTier: plan || '', // e.g., Basic, Premium, etc.
                nextRenewalDate: nextRenewalDate, // GMT date string
                language: language === 'en' ? 'En' : 'Ar', // Language preference
                channel: 'Web', // Channel of interaction
            });
        }
    );
}

/**
 * GenesysChatbox React Component
 *
 * This is the main React component that should be included in your application
 * to enable Genesys Web Messenger functionality. It handles the initialization
 * lifecycle and renders an empty span (the actual chat UI is injected by Genesys).
 *
 * The component:
 * - Initializes Genesys only once when mounted
 * - Uses the ready state to prevent duplicate initialization
 * - Returns a minimal span element as Genesys injects its own UI
 *
 */
export default function GenesysChatbox() {
    const { config } = useConfigurationSections();

    useEffect(() => {
        if (Genesys.ready || !config) return;
        initializeGenesys(config as { genesys?: GenesysConfigCMS });
    }, [config]);

    return <span />;
}

// ============================================================================
// TypeScript Type Definitions for Genesys Web Messenger SDK
// ============================================================================

declare global {
    interface Window {
        Genesys: GenesysFunction;
    }
}

/** Callback function type for Genesys operations */
type GenesysCallback = () => void;

/** Event callback function type with optional data parameter */
type GenesysEventCallback = (data?: any) => void;

/**
 * Main Genesys Function Interface
 *
 * This interface defines almost all possible ways to call the Genesys function,
 * providing comprehensive TypeScript support for the SDK.
 */
interface GenesysFunction {
    /** Initialization timestamp - set when Genesys is first loaded */
    t: number;

    /** Configuration object containing environment and deployment settings */
    c: any;

    /** Command queue - stores commands issued before the script is fully loaded */
    q: any[];

    // Plugin Management
    (command: 'registerPlugin', plugin: any, onSuccess?: GenesysCallback, onError?: GenesysCallback): boolean;

    // Command Execution
    (command: 'command', commandName: GenesysCommands, callback: GenesysCallback): boolean;
    (
        command: 'command',
        commandName: GenesysCommands,
        data?: any,
        onSuccess?: GenesysCallback,
        onError?: GenesysCallback
    ): boolean;

    // Event Management
    (command: 'subscribe', events: string | string[], callback: GenesysEventCallback): boolean;
    (command: 'monitor', callback: GenesysEventCallback): boolean;

    // Module Management
    (command: 'registerChild', child: any): boolean;
    (command: 'registerModule', module: any, config?: any): boolean;
    (command: 'executeQueue'): boolean;

    // Plugin Loading
    (
        command: 'loadPlugin',
        name: string,
        config: any,
        cache: boolean,
        onSuccess?: GenesysCallback,
        onError?: GenesysCallback
    ): boolean;
    (command: 'loadPlugin', name: string, config: any, onSuccess?: GenesysCallback, onError?: GenesysCallback): boolean;

    // File and Module Loading
    (command: 'loadFile', file: string, onSuccess?: GenesysCallback, onError?: GenesysCallback): boolean;
    (command: 'loadModule', module: string, onSuccess?: GenesysCallback, onError?: GenesysCallback): boolean;
    (command: 'loadJSON', json: string, onSuccess?: GenesysCallback, onError?: GenesysCallback): boolean;

    // Configuration
    (command: 'configure', config?: any): any;
    (command: 'lookup', key: string): any;

    // Fallback for any other command patterns
    (...args: any[]): boolean | any;
}

/**
 * Launcher Commands
 *
 * These commands control the chat launcher button visibility and behavior.
 * The launcher is the small chat icon that users click to open the messenger.
 */
type GenesysLauncherCommands =
    | 'Launcher.hide' // Hides the launcher button
    | 'Launcher.show' // Shows the launcher button
    | 'Launcher.toggle' // Toggles launcher visibility
    | 'Launcher.isVisible' // Returns current visibility state
    | 'Launcher.setVisibility'; // Sets visibility with parameter

/**
 * Messenger Commands
 *
 * These commands control the chat messenger window itself.
 * The messenger is the actual chat interface where conversations happen.
 */
type GenesysMessengerCommands =
    | 'Messenger.open' // Opens the chat messenger window
    | 'Messenger.close' // Closes the chat messenger window
    | 'Messenger.toggle' // Toggles messenger open/closed state
    | 'Messenger.isOpen' // Returns current open state
    | 'Messenger.clear'; // Clears the chat history/messages

/** Union type of all available Genesys commands */
type GenesysCommands = GenesysLauncherCommands | GenesysMessengerCommands;
